/**
 * TapPay 審核用揭露元件（金流安全文 + 平台法遵資訊）。
 * 文案常數集中此檔，公司資訊變更時只改這裡。
 *
 * 放置約定：
 * - TapPaySecurityNote → 刷卡／綁卡介面（審核項 6）
 * - PlatformLegalInfo → 帳號首頁底部（審核項 1–5）
 */
import { useState, type ReactNode } from "react";
import { Text, XStack, YStack, Stack } from "tamagui";
import { RiInformationLine, RiArrowRightSLine } from "react-icons/ri";
import { colors } from "@styles/colors";
import SheetWrapper from "@components/wrapper/SheetWrapper";

export const TAPPAY_MERCHANT = {
  companyName: "森智聯科技股份有限公司",
  taxId: "60335309",
  supportEmail: "service@artifactdev.tw",
} as const;

export const TAPPAY_LOGO_URL =
"https://lpg-public-assets.s3.ap-northeast-1.amazonaws.com/app/payment/TapPay_logo_%E6%A8%99%E6%BA%96%E6%A9%AB%E5%BC%8F.png";

/** TapPay PDF 建議之安全性說明（具名 TapPay + PCI + SSL）。 */
export const TAPPAY_SECURITY_COPY_LEAD =
  "本公司採用喬睿科技";
export const TAPPAY_SECURITY_COPY_REST =
  "金流交易系統，消費者刷卡時直接在銀行端系統中交易，本平台絕不留下您的完整信用卡資料。全程採用 SSL 2048bit 加密技術保護，並通過 PCI-DSS Level 1 國際安全認證。";
export const TAPPAY_SECURITY_COPY = `${TAPPAY_SECURITY_COPY_LEAD}${TAPPAY_SECURITY_COPY_REST}`;

export const FEE_COPY =
  "收費方式：綁定信用卡後，於結帳時依訂單金額扣款；貨到付款／月結則依各瓦斯行設定。";

/**
 * 退換貨與取消政策全文（金流審核項：需於平台明確揭露）。
 *
 * 依桶裝瓦斯的實際流程撰寫，與一般電商不同之處：
 * - 瓦斯由配送人員到府當場更換鋼瓶，「到貨」即等同「完成交付」，故爭議須在配送當下或極短時間內提出。
 * - 鋼瓶一經安裝／拆封籤即無法回收計量，這是公共安全與計量問題，不是商家不願退。
 * - 平台本身不出貨、不收款於自己名下：實際退換由各瓦斯行執行，平台負責受理與轉介。
 *
 * 文案異動請一併確認法務意見；此處為單一事實來源，勿在頁面各自複製。
 */
export const REFUND_POLICY_LEAD =
  "本平台提供線上訂購服務，商品交付、退換處理及各項費用（出車、安裝、拆除、搬運、退貨回收等）之收取與金額，均由承接該筆訂單之瓦斯行決定並收取。以下為一般處理原則，個別瓦斯行另有規定者，依其規定辦理。";

export const REFUND_POLICY: { title: string; items: string[] }[] = [
  {
    title: "一、訂單取消",
    items: [
      "訂單送出後由瓦斯行安排配送，無法於本平台自行取消；如需取消，請直接與您的瓦斯行聯繫（瓦斯行名稱與電話顯示於「瓦斯訂購」頁上方）。",
      "瓦斯行尚未出車前聯繫取消，通常不會產生費用。",
      "已出車或配送人員已抵達現場，可否取消及出車費用如何計算，由該瓦斯行認定。",
      "訂單完成配送後即無法取消，請改依下列退換貨規定辦理。",
    ],
  },
  {
    title: "二、桶裝瓦斯的退換貨",
    items: [
      "桶裝瓦斯由配送人員到府當場完成鋼瓶更換，請於配送當下確認封籤完整、外觀無損、重量無誤。",
      "如有封籤破損、外觀損傷、重量不足或漏氣疑慮，請於配送當下或收貨後 24 小時內聯繫瓦斯行；經確認屬商品或配送瑕疵者，由該瓦斯行更換、補足或退款。",
      "鋼瓶一經安裝使用或拆除封籤，基於公共安全且氣體無法回收計量，恕無法辦理退貨退款。",
      "未經安裝、封籤完整且未使用之鋼瓶，請於收貨後七日內聯繫瓦斯行申請退貨，由瓦斯行安排回收。",
    ],
  },
  {
    title: "三、配件與其他商品",
    items: [
      "瓦斯軟管、調整器等配件，於收貨後七日內，商品保持全新且完整包裝未經拆封（含內外包裝、贈品）者，可申請退貨。",
      "一經拆封、安裝或使用，因涉及燃氣安全，恕無法退貨。",
    ],
  },
  {
    title: "四、安裝與出車費用",
    items: [
      "安裝、拆除、搬運及出車費用並非商品之對價，一經提供即應收取，縱使商品事後退貨退款仍不退還；實際項目與金額由該瓦斯行認定。",
    ],
  },
  {
    title: "五、退款方式與時間",
    items: [
      "信用卡付款：退款將刷退至原信用卡，實際入帳時間依發卡銀行作業，約 3–7 個工作日。",
      "貨到付款／月結：由瓦斯行以現金退還，或於當期帳單扣抵，實際方式由該瓦斯行安排。",
      "發票由瓦斯行開立，辦理退貨時請連同發票一併退回；已索取紙本發票者請妥善保管。",
    ],
  },
  {
    title: "六、聯繫方式",
    items: [
      "訂單取消、退換貨等問題，請直接與您的瓦斯行聯繫，聯絡電話顯示於「瓦斯訂購」頁上方。",
      `無法解決時，請來信平台客服信箱：${TAPPAY_MERCHANT.supportEmail}，我們將協助處理。`,
    ],
  },
];

/** 刷卡介面：官方安全文，logo 接在首句後（低調中性）。 */
export function TapPaySecurityNote({ compact = false }: { compact?: boolean }) {
  const fontSize = compact ? 11 : 12;
  const lineHeight = compact ? 16 : 18;
  return (
    <YStack
      ai="flex-start"
      bg={colors.bgGray2}
      br={12}
      p={compact ? "$3" : "$3.5"}
      bw={1}
      bc={colors.borderLight}
    >
      <Text
        fontSize={fontSize}
        color={colors.sub}
        fontWeight="400"
        lineHeight={lineHeight}
      >
        {TAPPAY_SECURITY_COPY_LEAD}
        <img
          src={TAPPAY_LOGO_URL}
          alt="TapPay"
          style={{
            height: compact ? 14 : 16,
            width: "auto",
            display: "inline-block",
            verticalAlign: "middle",
            margin: "0 4px 2px",
            opacity: 0.85,
          }}
        />
        {TAPPAY_SECURITY_COPY_REST}
      </Text>
    </YStack>
  );
}

/**
 * 「電子支付由 TapPay 提供」一行文＋官方 logo。
 * 帳號頁「平台與金流資訊」與練習站「商店資訊」共用，讓審核與客戶都看得到金流商是誰。
 */
export function TapPayProviderLine({ fontSize = 12 }: { fontSize?: number } = {}) {
  return (
    <XStack ai="center" gap={6} flexWrap="wrap">
      <Text fontSize={fontSize} color={colors.sub} lineHeight={18}>
        電子支付（信用卡）由
      </Text>
      <img
        src={TAPPAY_LOGO_URL}
        alt="TapPay"
        style={{ height: fontSize + 4, width: "auto", display: "inline-block", verticalAlign: "middle", opacity: 0.9 }}
      />
      <Text fontSize={fontSize} color={colors.sub} lineHeight={18}>
        提供金流服務
      </Text>
    </XStack>
  );
}

/** 詳情內容（sheet / 展開共用）。 */
export function PlatformLegalDetails() {
  const { companyName, taxId, supportEmail } = TAPPAY_MERCHANT;
  return (
    <YStack gap="$3">
      <YStack gap="$1">
        <Text fontSize={15} fontWeight="800" color={colors.text}>
          {companyName}
        </Text>
        <Text fontSize={12} color={colors.sub}>
          統一編號 {taxId}
        </Text>
      </YStack>
      <YStack gap="$2">
        <Text fontSize={12} color={colors.text} lineHeight={18}>
          客服信箱：{supportEmail}
        </Text>
        <TapPayProviderLine />
        <Text fontSize={12} color={colors.sub} lineHeight={18}>
          {FEE_COPY}
        </Text>
      </YStack>
      <RefundPolicySection />
    </YStack>
  );
}

/**
 * 退換貨與取消政策全文區塊。
 * 內容長，預設收合成一列；審核與客戶都能在此展開看到完整條文。
 */
/** 政策內文（帳號頁展開與結帳頁 sheet 共用，避免兩份排版各自漂移）。 */
export function RefundPolicyBody() {
  return (
    <YStack gap="$3">
      <Text fontSize={12} color={colors.sub} lineHeight={18}>
        {REFUND_POLICY_LEAD}
      </Text>
      {REFUND_POLICY.map((section) => (
        <YStack key={section.title} gap="$1.5">
          <Text fontSize={12.5} fontWeight="700" color={colors.text}>
            {section.title}
          </Text>
          {section.items.map((item, i) => (
            <XStack key={i} gap="$2" ai="flex-start">
              <Text fontSize={12} color={colors.hint} lineHeight={18}>
                ・
              </Text>
              <Text fontSize={12} color={colors.sub} lineHeight={18} f={1}>
                {item}
              </Text>
            </XStack>
          ))}
        </YStack>
      ))}
    </YStack>
  );
}

/** 帳號頁：可展開的政策區塊。 */
export function RefundPolicySection() {
  const [open, setOpen] = useState(false);
  return (
    <YStack borderTopWidth={1} borderTopColor={colors.borderLight} pt="$3">
      <XStack
        ai="center"
        jc="space-between"
        cursor="pointer"
        pressStyle={{ opacity: 0.85 }}
        onPress={() => setOpen((v) => !v)}
        accessibilityRole="button"
        accessibilityLabel="退換貨與取消政策">
        <Text fontSize={13} fontWeight="700" color={colors.text}>
          退換貨與取消政策
        </Text>
        <RiArrowRightSLine
          size={16}
          color={colors.hint}
          style={{
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      </XStack>
      <AccordionBody open={open} divider={false}>
        <RefundPolicyBody />
      </AccordionBody>
    </YStack>
  );
}
/** 高度收合（grid 0fr/1fr，與 RefundCard 同套路，避免 mount 跳動） */
function AccordionBody({ open, children, divider = true }: { open: boolean; children: ReactNode; divider?: boolean }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateRows: open ? "1fr" : "0fr",
        transition: "grid-template-rows 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div style={{ overflow: "hidden", minHeight: 0 }}>
        <div
          style={{
            opacity: open ? 1 : 0,
            transform: open ? "translateY(0)" : "translateY(-4px)",
            transition: "opacity 0.22s ease, transform 0.22s ease",
            pointerEvents: open ? "auto" : "none",
            borderTop: divider ? `1px solid ${colors.borderLight}` : undefined,
            padding: divider ? "14px 14px 16px" : "10px 0 4px",
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * 帳號頁底部：對齊優惠券／付款方式 ListContainer 入口列；點擊展開詳情。
 */
export function PlatformLegalInfo() {
  const [open, setOpen] = useState(false);
  const { companyName, taxId } = TAPPAY_MERCHANT;

  return (
    <YStack bg={colors.light} br={12} ov="hidden">
      <XStack
        ai="center"
        gap="$3"
        px="$3.5"
        py="$3.5"
        cursor="pointer"
        pressStyle={{ opacity: 0.85 }}
        onPress={() => setOpen((v) => !v)}
        accessibilityRole="button"
        accessibilityLabel="平台與金流資訊"
      >
        <Stack
          w={40}
          h={40}
          br={12}
          ai="center"
          jc="center"
          backgroundColor={`${colors.primary}15`}
          flexShrink={0}
        >
          <RiInformationLine size={20} color={colors.primary} />
        </Stack>
        <YStack f={1} miw={0}>
          <Text fontSize={15} fontWeight="700" color={colors.text}>
            平台與金流資訊
          </Text>
          <Text fontSize={12} color={colors.sub} mt="$0.5" numberOfLines={1}>
            {companyName} · 統編 {taxId}
          </Text>
        </YStack>
        <RiArrowRightSLine
          size={18}
          color={colors.hint}
          style={{
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
            transition: "transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        />
      </XStack>

      <AccordionBody open={open}>
        <PlatformLegalDetails />
      </AccordionBody>
    </YStack>
  );
}

/** @deprecated 改用 PlatformLegalInfo；保留別名避免舊 import 瞬斷 */
export const PaymentLegalFooter = PlatformLegalInfo;

/**
 * 結帳頁揭露：一行摘要 + 就地展開全文。
 * 下單前告知（消保法與金流審核都看結帳流程），內容與帳號頁同一份 REFUND_POLICY，不另抄文案。
 */
export function RefundPolicyNote({ mt = "$5" }: { mt?: number | string } = {}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Text fontSize={12} color={colors.sub} lineHeight={18} mt={mt as never} mb="$2" px="$1">
        下單即視為您已閱讀並同意{" "}
        <Text
          fontSize={12}
          color={colors.primary}
          cursor="pointer"
          pressStyle={{ opacity: 0.7 }}
          onPress={() => setOpen(true)}
          accessibilityRole="button">
          退換貨與取消政策
        </Text>
        。
      </Text>
      <SheetWrapper isOpen={open} onOpenChange={setOpen} title="退換貨與取消政策" h={92}>
        <YStack px="$4" py="$4" pb="$8">
          <RefundPolicyBody />
        </YStack>
      </SheetWrapper>
    </>
  );
}

